import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Button } from "@ann/web-components";
import { PermissionsContainer, PermissionsHeader, PermissionsHeaderActions, PermissionsContentWrapper, PermissionItem } from "./Permissions.styled";
import { ExpandableContentCard } from "../../components";

// Mock data for permissions categories
const PERMISSIONS_MOCK_DATA = [
  {
    id: "agents",
    title: "Agents",
    subtitle: "4 permissions · 3 active, 1 disabled",
    icon: "lock_open", // Original icon
    defaultExpanded: true,
    permissions: [
      { name: "Edit Knowledge", description: "· Modify agent responses and learning content. (Rooftop Elevator)" },
      { name: "Duplicate Agents", description: "· Create copies of existing agents to reuse configurations. (Department)" },
      { name: "Delete Agents", description: "· Remove agents permanently from the workspace. (None)" },
    ],
  },
  {
    id: "automation",
    title: "Automation",
    subtitle: "3 permissions · 3 active",
    icon: "", // Add icon name here
    defaultExpanded: false,
    permissions: [
      { name: "Create Workflows", description: "· Build automated workflows for tasks. (All Departments)" },
      { name: "Edit Workflows", description: "· Modify existing automation rules. (Department)" },
      { name: "Run Automations", description: "· Execute automated processes manually. (Rooftop Elevator)" },
    ],
  },
  {
    id: "data",
    title: "Data",
    subtitle: "3 permissions · 3 active, 1 disabled",
    icon: "", // Add icon name here
    defaultExpanded: false,
    permissions: [
      { name: "Export Data", description: "· Download data in various formats. (All Departments)" },
      { name: "Import Data", description: "· Upload and process external data files. (Department)" },
      { name: "View Reports", description: "· Access analytics and reporting dashboards. (Rooftop Elevator)" },
    ],
  },
  {
    id: "drives",
    title: "Drives",
    subtitle: "4 permissions · 3 active, 1 disabled",
    icon: "", // Add icon name here
    defaultExpanded: false,
    permissions: [
      { name: "Upload Files", description: "· Add new files to shared drives. (All Departments)" },
      { name: "Download Files", description: "· Save files from drives to local storage. (Department)" },
      { name: "Delete Files", description: "· Remove files permanently from drives. (None)" },
      { name: "Share Files", description: "· Grant access to files with other users. (Rooftop Elevator)" },
    ],
  },
  {
    id: "marketing",
    title: "Marketing",
    subtitle: "4 permissions · 3 active, 1 disabled",
    icon: "", // Add icon name here
    defaultExpanded: false,
    permissions: [
      { name: "Create Campaigns", description: "· Design and launch marketing campaigns. (All Departments)" },
      { name: "Edit Campaigns", description: "· Modify active or draft campaigns. (Department)" },
      { name: "View Analytics", description: "· Access campaign performance metrics. (Rooftop Elevator)" },
      { name: "Delete Campaigns", description: "· Remove campaigns from the system. (None)" },
    ],
  },
  {
    id: "users",
    title: "Users",
    subtitle: "4 permissions · 3 active, 1 disabled",
    icon: "", // Add icon name here
    defaultExpanded: false,
    permissions: [
      { name: "Invite Users", description: "· Send invitations to new team members. (All Departments)" },
      { name: "Edit User Roles", description: "· Modify user permissions and access levels. (Department)" },
      { name: "View User Activity", description: "· Monitor user actions and login history. (Rooftop Elevator)" },
      { name: "Deactivate Users", description: "· Disable user accounts temporarily or permanently. (None)" },
    ],
  },
];

const Permissions = ({ roleId }) => {
  const navigate = useNavigate();
  
  // Initialize expanded state based on defaultExpanded values
  const [expandedCards, setExpandedCards] = useState(() => {
    const initial = {};
    PERMISSIONS_MOCK_DATA.forEach((category) => {
      initial[category.id] = category.defaultExpanded;
    });
    return initial;
  });

  const allExpanded = PERMISSIONS_MOCK_DATA.every((category) => expandedCards[category.id]);

  const handleEditPermissions = useCallback(() => {
    if (roleId) {
      navigate(`/v2/user-management/permissions/${roleId}`);
    } else {
      navigate('/v2/user-management/permissions/default');
    }
  }, [navigate, roleId]);

  const handleExpandAll = useCallback(() => {
    setExpandedCards((prev) => {
      const newState = {};
      const shouldExpand = !allExpanded;
      PERMISSIONS_MOCK_DATA.forEach((category) => {
        newState[category.id] = shouldExpand;
      });
      return newState;
    });
  }, [allExpanded]);

  const handleToggleCard = useCallback((categoryId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  }, []);

  return (
    <PermissionsContainer>
      <PermissionsHeader>
        <Typography category="subtitle" size="small" weight="bold">
          Permissions overview
        </Typography>
        <Button
          label="Edit permissions"
          size="small"
          type="primary"
          onClick={handleEditPermissions}
        />
      </PermissionsHeader>
      <PermissionsHeaderActions>
        <Button
          label="View all the details"
          size="small"
          type="secondary"
        />
        <Button
          label={allExpanded ? "Collapse all" : "Expand all"}
          size="small"
          type="tertiary"
          onClick={handleExpandAll}
          disableEffects={true}
        />
      </PermissionsHeaderActions>
      <PermissionsContentWrapper>
        {PERMISSIONS_MOCK_DATA.map((category) => (
          <ExpandableContentCard
            key={category.id}
            title={category.title}
            subtitle={category.subtitle}
            icon={category.icon}
            isExpanded={expandedCards[category.id]}
            onToggle={() => handleToggleCard(category.id)}
          >
            {category.permissions.map((permission, index) => (
              <PermissionItem key={index}>
                <Typography category="body" size="small" weight="regular">
                  {permission.name}
                </Typography>
                <Typography category="body" size="small" weight="regular" color="body">
                  {permission.description}
                </Typography>
              </PermissionItem>
            ))}
          </ExpandableContentCard>
        ))}
      </PermissionsContentWrapper>
    </PermissionsContainer>
  );
};

export default Permissions;

